import { ReactNode, useState } from 'react';
import { Alert, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { Plus, Search } from 'lucide-react';
import { PrimaryButton } from '../../../../shared/ui/Inputs';
import { Spinner } from '../../../../shared/ui/Spinners';

type BasketListScreenProps = {
  title: string;
  description: string;
  /** Texto do botão principal, ex.: "Nova pergunta". Sem ele, a tela não tem botão. */
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  searchPlaceholder: string;
  loading?: boolean;
  loadingLabel?: string;
  error?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** Avisos que ficam acima da tabela (ex.: "crie um fluxo antes"). */
  notice?: ReactNode;
  /** Recebe o texto da busca para a tabela filtrar. */
  children: (search: string) => ReactNode;
};

/** Esqueleto das abas de Balaios: título, ação principal, busca e conteúdo. */
export const BasketListScreen = ({
  title,
  description,
  actionLabel,
  onAction,
  actionDisabled = false,
  searchPlaceholder,
  loading = false,
  loadingLabel,
  error,
  onRetry,
  retryLabel = 'Tentar novamente',
  notice,
  children,
}: BasketListScreenProps) => {
  const [search, setSearch] = useState('');

  if (loading) {
    return (
      <div
        role="status"
        aria-label={loadingLabel ?? `Carregando ${title.toLowerCase()}`}
        style={{
          minHeight: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Typography variant="h6" fontWeight={700} component="h2">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </div>
        {actionLabel && (
          <PrimaryButton
            type="button"
            onClick={onAction}
            disabled={actionDisabled}
            style={{
              height: 40,
              paddingLeft: 18,
              paddingRight: 18,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Plus size={16} />
            {actionLabel}
          </PrimaryButton>
        )}
      </div>

      {error && (
        <Alert
          severity="error"
          action={
            onRetry && (
              <Button color="inherit" size="small" onClick={onRetry}>
                {retryLabel}
              </Button>
            )
          }
        >
          {error}
        </Alert>
      )}

      {notice}

      <TextField
        size="small"
        fullWidth
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={searchPlaceholder}
        inputProps={{ 'aria-label': searchPlaceholder }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} />
            </InputAdornment>
          ),
        }}
      />

      {children(search)}
    </div>
  );
};
